<template>
  <div class="xufei-page">
    <!-- 提示消息 -->
    <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>
    <div v-if="errorMessage" class="alert alert-error">{{ errorMessage }}</div>

    <div class="Nai-tsO-5T4Pp-Long">
      <!-- 返回箭头和标题 -->
      <div class="header-with-back">
        <svg 
          class="back-arrow" 
          width="18" 
          height="18" 
          viewBox="0 0 1024 1024" 
          version="1.1" 
          xmlns="http://www.w3.org/2000/svg" 
          @click="goBack"
        >
          <path d="M778.671 926.323a56.811 56.811 0 1 1-80.33 80.331L243.85 552.165a56.811 56.811 0 0 1 0-80.33l454.49-454.49a56.811 56.811 0 1 1 80.33 80.331L364.348 512 778.67 926.323z"></path>
        </svg>
        <div class="page-title">续费</div>
      </div>

      <!-- 用户信息 -->
      <div class="user-info">
        <div><strong>用户名：</strong>{{ userInfo.username }}</div>
        <div><strong>当前余额：</strong>{{ formatBalance(userInfo.balance) }} 元</div>
        <div><strong>到期时间：</strong>{{ formatExpireTime(userInfo.expireTime) }}</div>
        <div><strong>剩余天数：</strong>{{ userInfo.expireDays < 0 ? '永不过期' : userInfo.expireDays + ' 天' }}</div>
      </div>

      <div class="Nai-DlEBBMYId-Long" style="padding-left: 15px; padding-right: 15px;">
        <form @submit.prevent="handleSubmit">
          <div class="Nai-foou8HLzZ-Long">
            <!-- 续费天数显示在滑块旁边 -->
            <div class="days-display-container">
              <div class="days-label">续费天数</div>
              <div class="days-value">{{ renewDays }}</div>
            </div>

            <!-- Vue Slider 样式滑块 -->
            <div 
              class="vue-slider vue-slider-ltr" 
              ref="sliderRef"
              style="padding-top: 20px;"
              @click="handleSliderClick"
            >
              <div class="vue-slider-rail">
                <div 
                  class="vue-slider-process" 
                  :style="{ width: sliderPercent + '%' }"
                ></div>
                <div class="vue-slider-marks">
                  <div class="vue-slider-mark vue-slider-mark-active" style="left: 0%;">
                    <div class="vue-slider-mark-step vue-slider-mark-step-active"></div>
                    <div class="vue-slider-mark-label vue-slider-mark-label-active">😡</div>
                  </div>
                  <div class="vue-slider-mark vue-slider-mark-active" style="left: 33%;">
                    <div class="vue-slider-mark-step vue-slider-mark-step-active"></div>
                    <div class="vue-slider-mark-label vue-slider-mark-label-active">😑</div>
                  </div>
                  <div class="vue-slider-mark vue-slider-mark-active" style="left: 66%;">
                    <div class="vue-slider-mark-step vue-slider-mark-step-active"></div>
                    <div class="vue-slider-mark-label vue-slider-mark-label-active">😊</div>
                  </div>
                  <div class="vue-slider-mark vue-slider-mark-active" style="left: 100%;">
                    <div class="vue-slider-mark-step vue-slider-mark-step-active"></div>
                    <div class="vue-slider-mark-label vue-slider-mark-label-active">😆</div>
                  </div>
                </div>
                <div 
                  class="vue-slider-dot vue-slider-dot-hover"
                  :style="{ left: sliderPercent + '%' }"
                  role="slider"
                  :aria-valuenow="renewDays"
                  aria-valuemin="1"
                  aria-valuemax="60"
                  aria-orientation="horizontal"
                  tabindex="0"
                  @mousedown="startDrag"
                  @touchstart="startDrag"
                  @mouseenter="showTooltip = true"
                  @mouseleave="showTooltip = false"
                >
                  <div class="vue-slider-dot-handle"></div>
                  <div 
                    class="vue-slider-dot-tooltip vue-slider-dot-tooltip-top"
                    :class="{ 'vue-slider-dot-tooltip-show': showTooltip || isDragging }"
                  >
                    <div class="vue-slider-dot-tooltip-inner vue-slider-dot-tooltip-inner-top">
                      <span class="vue-slider-dot-tooltip-text">{{ renewDays }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 费用计算显示 -->
          <div class="cost-summary">
            <div class="Nai-kZs8wkrXj-Long" style="padding-top: 3px;">
              <span>{{ renewDays }}</span> × {{ pricePerDay }} = 
              <span class="red-text">{{ totalCost }}</span> 元
            </div>
            <div v-if="userInfo.balance < totalCost" class="balance-warning">
              余额不足，请先充值
            </div>
          </div>

          <!-- 支付按钮 -->
          <button 
            type="submit" 
            class="payment-btn" 
            :disabled="isSubmitting || userInfo.balance < totalCost"
            style="padding-top: 12px; margin-top: 100px;"
          >
            <span v-if="isSubmitting">处理中...</span>
            <span v-else>
              支付 <span class="red-text">{{ totalCost }}</span> 元续费 
              <span>{{ renewDays }}</span> 天
            </span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { apiFetch } from '@/config/api'

export default {
  name: 'XufeiPage',
  emits: ['close'],
  setup(props, { emit }) {
    // 常量
    const pricePerDay = 3
    const minDays = 1
    const maxDays = 60

    // 状态
    const userInfo = reactive({
      username: '',
      balance: 0,
      expireTime: null,
      expireDays: 0
    })

    const renewDays = ref(30)
    const isDragging = ref(false)
    const showTooltip = ref(false)
    const isSubmitting = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const sliderRef = ref(null)

    // 计算属性
    const totalCost = computed(() => renewDays.value * pricePerDay)
    
    const sliderPercent = computed(() => {
      return ((renewDays.value - minDays) / (maxDays - minDays)) * 100
    })

    // 滑块方法
    const percentToDays = (percent) => {
      return Math.round(minDays + (percent / 100) * (maxDays - minDays))
    }

    const handleSliderClick = (e) => {
      if (!sliderRef.value) return
      const rect = sliderRef.value.getBoundingClientRect()
      let percent = ((e.clientX - rect.left) / rect.width) * 100
      percent = Math.max(0, Math.min(100, percent))
      renewDays.value = percentToDays(percent)
    }

    const startDrag = (e) => {
      isDragging.value = true
      e.preventDefault()
    }

    const handleMove = (clientX) => {
      if (!isDragging.value || !sliderRef.value) return
      const rect = sliderRef.value.getBoundingClientRect()
      let percent = ((clientX - rect.left) / rect.width) * 100
      percent = Math.max(0, Math.min(100, percent))
      renewDays.value = percentToDays(percent)
    }

    const handleMouseMove = (e) => {
      handleMove(e.clientX)
    }

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX)
      }
    }

    const stopDrag = () => {
      isDragging.value = false
    }

    // 方法
    const formatBalance = (balance) => {
      return Number(balance || 0).toFixed(2)
    }

    const formatExpireTime = (expireTime) => {
      if (!expireTime) return '未设置'
      const date = new Date(expireTime)
      if (isNaN(date.getTime())) return '未设置'
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }

    const goBack = () => {
      emit('close')
    }

    const clearMessages = () => {
      setTimeout(() => {
        successMessage.value = ''
        errorMessage.value = ''
      }, 3000)
    }

    const handleSubmit = async () => {
      const cost = totalCost.value
      const confirmMsg = `确认支付 ¥${cost} 续费 ${renewDays.value} 天？`
      
      if (!confirm(confirmMsg)) {
        return
      }

      if (renewDays.value <= 0) {
        errorMessage.value = '请选择有效的续费天数'
        clearMessages()
        return
      }

      if (userInfo.balance < cost) {
        errorMessage.value = `余额不足，当前余额: ¥${formatBalance(userInfo.balance)}，需要: ¥${cost}`
        clearMessages()
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''
      successMessage.value = ''

      try {
        const result = await apiFetch('/api/user/renew', {
          method: 'POST',
          body: JSON.stringify({
            renewDays: renewDays.value
          })
        })

        if (result.code === 0) {
          successMessage.value = `续费成功！已续费 ${renewDays.value} 天，花费 ¥${cost}`
          // 更新用户信息
          userInfo.balance = result.data.newBalance
          userInfo.expireTime = result.data.newExpireTime
          userInfo.expireDays = result.data.newExpireDays
          clearMessages()
        } else {
          errorMessage.value = result.msg || '续费失败，请重试'
          clearMessages()
        }
      } catch (error) {
        console.error('续费请求失败:', error)
        errorMessage.value = '网络错误，请重试'
        clearMessages()
      } finally {
        isSubmitting.value = false
      }
    }

    const fetchUserInfo = async () => {
      try {
        const result = await apiFetch('/api/user/info')
        
        if (result.code === 0) {
          userInfo.username = result.data.username
          userInfo.balance = result.data.balance
          userInfo.expireTime = result.data.expireTime
          userInfo.expireDays = result.data.expireDays
        } else {
          errorMessage.value = result.msg || '获取用户信息失败'
          clearMessages()
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        errorMessage.value = '网络错误，请重试'
        clearMessages()
      }
    }

    // 生命周期
    onMounted(() => {
      fetchUserInfo()
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', stopDrag)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', stopDrag)
    })

    onUnmounted(() => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', stopDrag)
    })

    return {
      // 常量
      pricePerDay,
      // 状态
      userInfo,
      renewDays,
      isDragging,
      showTooltip,
      isSubmitting,
      successMessage,
      errorMessage,
      sliderRef,
      // 计算属性
      totalCost,
      sliderPercent,
      // 方法
      formatBalance,
      formatExpireTime,
      handleSliderClick,
      startDrag,
      goBack,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.xufei-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.alert {
  padding: 10px;
  margin: 10px;
  border-radius: 4px;
  text-align: center;
}

.alert-success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.user-info {
  background: #e8f5e8;
  padding: 15px;
  margin: 10px;
  border-radius: 5px;
}

/* 返回箭头和标题样式 */
.header-with-back {
  display: flex;
  align-items: center;
  padding: 10px;
}

.back-arrow {
  cursor: pointer;
  margin-right: 12px;
}

.page-title {
  font-weight: bold;
}

/* 续费天数显示样式 */
.days-display-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.days-label {
  font-weight: bold;
}

.days-value {
  font-weight: bold;
  color: #007bff;
  font-size: 18px;
}

/* Vue Slider 样式 */
.vue-slider {
  position: relative;
  box-sizing: content-box;
  user-select: none;
  display: block;
  padding: 10px 0;
  width: 100%;
  height: 4px;
}

.vue-slider-rail {
  background: #e5e5e5;
  border-radius: 15px;
  width: 100%;
  height: 100%;
  position: relative;
}

.vue-slider-process {
  position: absolute;
  z-index: 1;
  background: #007bff;
  border-radius: 15px;
  height: 100%;
  top: 0;
  left: 0;
  transition: width 0.1s;
}

.vue-slider-marks {
  position: relative;
  width: 100%;
  height: 100%;
}

.vue-slider-mark {
  position: absolute;
  z-index: 1;
  height: 100%;
  width: 4px;
  transform: translate(-50%, -50%);
  top: 50%;
}

.vue-slider-mark-step {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #999;
  position: absolute;
  top: 0;
}

.vue-slider-mark-label {
  position: absolute;
  top: 100%;
  margin-top: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  white-space: nowrap;
}

.vue-slider-dot {
  position: absolute;
  transition: left 0.1s;
  z-index: 5;
  width: 20px;
  height: 20px;
  transform: translate(-50%, -50%);
  top: 50%;
}

.vue-slider-dot-handle {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #007bff;
  box-shadow: 0.5px 0.5px 2px 1px rgba(0, 0, 0, 0.32);
  cursor: pointer;
}

.vue-slider-dot-tooltip {
  position: absolute;
  visibility: hidden;
}

.vue-slider-dot:hover .vue-slider-dot-tooltip,
.vue-slider-dot-tooltip-show {
  visibility: visible;
}

.vue-slider-dot-tooltip-top {
  top: -10px;
  left: 50%;
  transform: translate(-50%, -100%);
}

.vue-slider-dot-tooltip-inner {
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
}

.vue-slider-dot-tooltip-inner-top {
  transform: translateY(-5px);
}

.payment-btn {
  background: #00b15e;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  margin-top: 20px;
}

.payment-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.red-text {
  color: #000000;
  font-weight: bold;
}

.cost-summary {
  text-align: center;
  margin: 20px 0;
  font-size: 16px;
}

.balance-warning {
  margin-top: 10px;
  color: #f44336;
  font-size: 13px;
}
</style>








